import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import {
    createInputSwitchDefaultValues,
    DEMO_DIARY_ID,
    DEMO_ENTRY_ID,
    DEMO_TRACKER_ID,
    INPUT_SWITCH_DEMOS,
    type InputSwitchDemoKey,
} from "@/features/developer/fixtures/uiDemos";
import InputSwitch from "@/features/inputs/components/InputSwitch";
import { useNav } from "@/utils/nav";
import type { InputT } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

export const Route = createFileRoute("/_app/design/forms/inputs")({
    component: FormInputsPage,
});

interface ExampleText {
    label: string;
    caption?: string;
    description: string;
    error: string;
}

const DEFAULT_ERROR = "Answer this question to continue";

/** Choice questions that let the patient add their own option. Previewed, so added options stay in this browser. */
const ADDABLE_OPTION_KEYS = new Set<InputSwitchDemoKey>(["single-choice", "multiple-choice"]);

/** Patient-facing example wording, so every input shows a label, caption, description and error. */
const EXAMPLE_TEXT: Partial<Record<InputSwitchDemoKey, ExampleText>> = {
    "short-text": {
        label: "What is the name of your surgeon?",
        caption: "About your operation",
        description: "You can find this on your appointment letter.",
        error: "Enter the name of your surgeon",
    },
    "long-text": {
        label: "Is there anything else you would like to tell us?",
        description: "For example, a new symptom or a change in your medicines.",
        error: "Tell us anything else you would like us to know",
    },
    "date-picker": {
        label: "When did your pain start?",
        description: "If you are not sure, give your best guess.",
        error: "Enter the date your pain started",
    },
    "date-time-picker": {
        label: "When did you last take a painkiller?",
        description: "Include the time if you can remember it.",
        error: "Enter the date and time you last took a painkiller",
    },
    number: {
        label: "What is your weight?",
        description: "Use your most recent weight in kilograms.",
        error: "Enter your weight in kilograms",
    },
    "yes-no": {
        label: "Have you had a fall in the last month?",
        caption: "Your safety",
        description: "Include any trip or slip, even if you were not hurt.",
        error: "Select yes or no",
    },
    checkbox: {
        label: "I have read the information about my operation",
        description: "Tick the box to confirm.",
        error: "Tick the box to confirm you have read the information",
    },
    dropdown: {
        label: "Which knee was operated on?",
        caption: "Your operation",
        description: "Choose one option.",
        error: "Select which knee was operated on",
    },
    duration: {
        label: "How long have you had this problem?",
        description: "Enter the number and choose days, weeks, months or years.",
        error: "Enter how long you have had this problem",
    },
    email: {
        label: "What is your email address?",
        description: "We only use this to send you reminders about your forms.",
        error: "Enter an email address in the correct format, like name@example.com",
    },
    "address-autocomplete": {
        label: "What is your home address?",
        description: "Start typing your postcode or the first line of your address.",
        error: "Enter your home address",
    },
    slider: {
        label: "How well can you move your knee today?",
        description: "0 means you cannot move it at all. 100 means it moves normally.",
        error: "Move the slider to choose a number",
    },
    "single-choice": {
        label: "How would you describe your knee pain?",
        caption: "Pain",
        description: "Think about the last 7 days.",
        error: "Select how you would describe your knee pain",
    },
    "scale-rating": {
        label: "How much does pain stop you from sleeping?",
        caption: "Sleep",
        description: "Think about the last 7 days.",
        error: "Select how much pain stops you from sleeping",
    },
    star: {
        label: "How happy are you with the result of your operation?",
        caption: "Your result",
        description: "1 star is very unhappy. 5 stars is very happy.",
        error: "Select a number of stars",
    },
    "multiple-choice": {
        label: "Which of these symptoms have you had?",
        caption: "Symptoms",
        description: "Select all that apply.",
        error: "Select at least one symptom",
    },
    photo: {
        label: "Take a photo of your wound",
        description: "Use good light and hold your phone about 30cm away.",
        error: "Add a photo of your wound",
    },
    "foot-photos": {
        label: "Take photos of your feet",
        description: "We will guide you through each photo.",
        error: "Add photos of your feet",
    },
    "month-year": {
        label: "When did you have your operation?",
        description: "Enter the month and year, for example 03 2025.",
        error: "Enter the month and year of your operation",
    },
    "repeatable-group": {
        label: "List the treatments you have tried",
        description: "Add one row for each treatment.",
        error: "Add at least one treatment",
    },
    "pain-slider": {
        label: "How bad is your pain right now?",
        description: "0 is no pain. 10 is the worst pain you can imagine.",
        error: "Move the slider to rate your pain",
    },
    "glucose-slider": {
        label: "What was your last blood sugar reading?",
        description: "Enter the reading in mmol/L.",
        error: "Move the slider to enter your reading",
    },
    "tried-treatment": {
        label: "Have you tried physiotherapy for this problem?",
        description: "Include NHS and private physiotherapy.",
        error: "Tell us if you have tried physiotherapy",
    },
    "ratings-list": {
        label: "Rate how hard these activities are for you",
        description: "Choose up to 3 activities. 0 is no difficulty and 10 is unable to do.",
        error: "Rate at least one activity",
    },
    "ms-adl-ratings-list": {
        label: "Rate your difficulty with each activity of daily living",
        description: "0 is no difficulty and 10 is unable to do.",
        error: "Rate each activity you chose",
    },
    "ms-adl-selection": {
        label: "Which activities of daily living are important to you?",
        description: "Choose between 1 and 3 activities.",
        error: "Choose at least one activity",
    },
};

function withExampleText(inputKey: InputSwitchDemoKey, input: InputT): { input: InputT; error: string } {
    const example = EXAMPLE_TEXT[inputKey];
    const addable = ADDABLE_OPTION_KEYS.has(inputKey)
        ? { allow_add_option: true, add_option_label: "Something else" }
        : {};
    if (!example) {
        return { input: { ...input, ...addable }, error: DEFAULT_ERROR };
    }
    return {
        input: {
            ...input,
            ...addable,
            input_label: example.label,
            input_caption: example.caption ?? null,
            input_description: example.description,
        },
        error: example.error,
    };
}

function InputDemo({
    inputKey,
    input,
    error,
    showErrors,
}: {
    inputKey: InputSwitchDemoKey;
    input: InputT;
    error: string;
    showErrors: boolean;
}) {
    const form = useForm({ defaultValues: createInputSwitchDefaultValues([input]) });
    const { setError, clearErrors } = form;

    useEffect(() => {
        if (showErrors) {
            setError(input.input_id, { type: "manual", message: error });
        } else {
            clearErrors(input.input_id);
        }
    }, [showErrors, input.input_id, error, setError, clearErrors]);

    return (
        <ui.Card>
            <ui.CardHeader className="pb-2">
                <code className="text-muted-foreground text-xs">{inputKey}</code>
            </ui.CardHeader>
            <ui.CardContent>
                <div data-form-text>
                    <FormProvider {...form}>
                        <InputSwitch
                            input={input}
                            form={form}
                            is_preview={inputKey === "foot-photos" || ADDABLE_OPTION_KEYS.has(inputKey)}
                            previewContext={{
                                entryId: DEMO_ENTRY_ID,
                                diaryId: DEMO_DIARY_ID,
                                trackerId: DEMO_TRACKER_ID,
                                pageIndex: "0",
                            }}
                        />
                    </FormProvider>
                </div>
            </ui.CardContent>
        </ui.Card>
    );
}

function FormInputsPage() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Forms", route: { to: "/design/forms" } },
        { label: "Inputs", route: { to: "/design/forms/inputs" } },
    ]);
    const [showErrors, setShowErrors] = useState(false);
    const demos = useMemo(
        () => INPUT_SWITCH_DEMOS.map(({ inputKey, input }) => ({ inputKey, ...withExampleText(inputKey, input) })),
        []
    );

    return (
        <AdminPageLayout
            title="Form inputs"
            description="Every input type a patient can answer, with an example label, caption and description. Switch on errors to see the error message under each one. Some inputs that fetch diary data (repeatable group, ratings list, photo) may appear empty outside a live diary."
            maxWidthClassName="max-w-2xl"
        >
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <ui.Switch id="show-errors" checked={showErrors} onCheckedChange={setShowErrors} />
                    <ui.Label htmlFor="show-errors">Show errors</ui.Label>
                </div>
                {demos.map(({ inputKey, input, error }) => (
                    <InputDemo key={inputKey} inputKey={inputKey} input={input} error={error} showErrors={showErrors} />
                ))}
            </div>
        </AdminPageLayout>
    );
}
